import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { formatDayLabel } from '@/features/scheduling/availability'
import { Text } from '@/ui/core/typography'
import { format } from 'date-fns'
import { TYPE_INSET } from './layout'

/**
 * The visit being moved, so the patient can see what they are giving up before they choose.
 *
 * One grey sentence, not a white card with a black tag over three lines.
 *
 * Every panel on this screen is something a member can act on: the week's days, a studio's
 * times, a time itself. This is the one thing on the page that is not — it is the state the page
 * is in — so it is said rather than boxed. As a white card a hundred and twenty points tall it
 * was the brightest and heaviest block on a page whose subject is the picker beneath it; on
 * blush it was still a panel claiming to be one of them.
 *
 * No glyph either. The clock was the only mark on this page not attached to something tappable,
 * and without it the line is a sentence rather than a component that lost its edges.
 *
 * No studio, and no address. The studio is not what is being changed — a member moving a visit
 * is moving a time — and naming it here put a word in the line that the week below is about to
 * offer five of. When it matters it is on the card the member came from and on the sheet they
 * are going to.
 *
 * The weight is on the verb. "Moving" is the word that says what the page is doing; the day and
 * the hour behind it are being left, not chosen, and setting them in ink made the line compete
 * with the week label directly under it, which is the date that matters.
 *
 * Through `formatDayLabel`, so the visit being left prints the way the one being taken does:
 * `startTimeFormatted` is written for the home card's own sentence — "Sep 29th at 12:30 PM" —
 * and against a confirm sheet reading "Tue, Sep 29" it was the same date in two different hands.
 *
 * At the type inset, so it starts on the same line as the week label under it and as a studio's
 * name inside its card. Only a panel begins at the page's edge.
 *
 * At the size the "no available times at…" footnote is set in, not the city switcher's. Both of
 * those are a step below the body, and for a moment it looked as though this line should match
 * the switcher, since the two bracket the week label. But the switcher is a control — bold,
 * underlined, with a chevron — and this is prose; what it actually belongs with is the other
 * grey sentence on the page saying what the picker is showing and why. Those two are the same
 * kind of thing, so they are the same size.
 */
export const RescheduleContext = ({
	visit,
	now,
}: { visit: UpcomingVisit; now: Date }) => (
	<Text size="body1" tone="soft" style={{ paddingHorizontal: TYPE_INSET }}>
		{/* Medium, not bold. The `weight` variant only offers book and bold — its `medium` is an
		    alias for book, kept for retirement — so the face is named directly, as the input and
		    the chat composer already do. Bold made the one word on this line heavier than
		    anything else above the fold, which is a lot of voice for a label. */}
		<Text size="body1" className="font-body-medium">
			Moving
		</Text>
		{` ${formatDayLabel(format(visit.startTimeLocal, 'yyyy-MM-dd'), now)} · ${format(
			visit.startTimeLocal,
			'h:mm a'
		)}`}
	</Text>
)
